import React, { PureComponent } from 'react';
import { Helmet } from 'react-helmet';

import LocalizedMessage, { localizeMessage } from '../../../../components/LocalizedMessage';
import URLFiltersHoc, { Props } from '../../../../components/URLFiltersHoc';
import Select from '../../../../components/Select';
import Breadcrumbs from '../../../../components/Breadcrumbs';
import TableList from '../../../../components/TableList';

import Alert from '../../../../helpers/alert';
import API from '../../../../api';
import { Locale } from 'types';

interface IOwnProps {
  lang: Locale;
}

interface IState {
  dataList: any[],
  dataListTotal: number;
  isLoading: boolean;

  countryOptions: any[];
  countryValue: any;
  country: any;
  region: any;
}

class Regions extends PureComponent<IOwnProps & Props, IState> {
  state: IState = {
    dataList: [],
    dataListTotal: 1,
    isLoading: false,

    countryOptions: [],

    countryValue: null,
    country: null,
    region: ''
  };

  mounted = false;
  refreshListTimer = 0;
  refreshListTimerDelay = 500;
  _form = React.createRef<HTMLFormElement>();

  componentDidMount () {
    this.mounted = true;
    this.loadData();
    this.refreshList();
  }

  componentDidUpdate (prevProps) {
    if (prevProps.filters !== this.props.filters) {
      this.refreshList();
    }
  }

  componentWillUnmount () {
    this.mounted = false;
    clearTimeout(this.refreshListTimer);
  }

  async loadData () {
    const [countriesResponse] = await Promise.all([
      API.countries.list({ max: 0 })
    ]);

    if (!this.mounted) {
      return;
    }

    const countryOptions = countriesResponse.items.map(item => ({
      value: item.id,
      name: item.name,
      nameEn: item.nameEn
    }));

    this.setState({
      countryOptions
    });
  }

  refreshList () {
    clearTimeout(this.refreshListTimer);
    const { currentPage, currentSort, max } = this.props.filters;
    const dataListRequest = {
      page: currentPage,
      order: currentSort,
      max,
    };

    this.refreshListTimer = window.setTimeout(async () => {
      this.setState({ isLoading: true });

      const dataList = await API.regions.list(dataListRequest);

      if (!this.mounted) {
        return;
      }

      this.setState({
        dataList: dataList.items,
        dataListTotal: dataList.total,
        isLoading: false
      });
    }, this.refreshListTimerDelay);
  }

  getBreadcrumbs () {
    return [
      {
        title: <LocalizedMessage id='home' />,
        link: '/app'
      },
      {
        title: <LocalizedMessage id='menu.dictionaries.regions' />,
        link: '/app/dictionaries/regions'
      }
    ];
  }

  setFormRef = (ref) => {
    this._form = ref;
  };

  handleInputChange = (e) => {
    const value = e.target.value;

    this.setState({
      region: value
    });
  };

  handleChangeCountrySelect = (country) => {
    this.setState({ country });
  };

  getSelectCountryConfig () {
    const { lang } = this.props;
    const { countryValue, countryOptions } = this.state;

    const countryFieldName = lang === 'en' ? 'nameEn' : 'name';

    return {
      isSearchable: false,
      value: countryValue,
      onChange: this.handleChangeCountrySelect,
      options: countryOptions.map(country => ({
        value: country.value,
        label: country[countryFieldName]
      })),
      placeholder: <LocalizedMessage id='regions-dictionary.country.placeholder' />
    };
  }

  resetClick = () => {
    if (this._form.current) {
      this._form.current.reset();
    }

    this.setState({
      region: null
    });
  };

  onSubmit = async (e) => {
    e.preventDefault();

    const {
      country,
      region
    } = this.state;

    if (!region || !region.length) {
      return;
    }

    try {
      await API.regions.create({
        countryId: country.value,
        name: region
      });

      Alert.success(localizeMessage({ id: 'regions-dictionary.successfullySent' }));

      this.resetClick();

      this.refreshList();
    } catch (error) {
      console.error(error);

      Alert.warn(localizeMessage({ id: 'regions-dictionary.errorSending' }));
    }
  };

  render () {
    const {
      onUpdateSort, onUpdatePage, filters, lang,
    } = this.props;
    const {
      country,
      region,
      dataList,
      dataListTotal,
      isLoading
    } = this.state;

    const countryFieldName = lang === 'en' ? 'nameEn' : 'name';

    return (
      <>
        <LocalizedMessage
          id='site.title.dictionaries.regions'
        >
          {localizedMessage => (
            <>
              <Helmet
                title={localizedMessage}
              />
              <Breadcrumbs
                title={localizedMessage}
                data={this.getBreadcrumbs()}
              />
            </>
          )}
        </LocalizedMessage>

        <div className='wrapper wrapper-content'>
          <div className='row'>
            <div className='col-lg-12'>
              <div className='ibox float-e-margins'>
                <div className='ibox-content'>
                  <form
                    ref={this.setFormRef}
                    onSubmit={this.onSubmit}
                  >
                    <div className='form-horizontal'>
                      <div className='form-group'>
                        <label
                          className='col-lg-2 control-label'
                          htmlFor='country'
                        >
                          <LocalizedMessage id='regions-dictionary.country.label' />
                        </label>
                        <div className='col-lg-10'>
                          <Select {...this.getSelectCountryConfig()} />
                        </div>
                      </div>

                      <div className='form-group'>
                        <label className='col-lg-2 control-label'>
                          <LocalizedMessage id='regions-dictionary.region.label' />
                        </label>

                        <div className='col-lg-10'>
                          <LocalizedMessage id='regions-dictionary.region.placeholder'>
                            {localizedPlaceholder => (
                              <input
                                type='text'
                                className='form-control'
                                name='region'
                                value={region}
                                placeholder={localizedPlaceholder}
                                onChange={this.handleInputChange}
                              />
                            )}
                          </LocalizedMessage>
                        </div>
                      </div>

                      <div className='hr-line-dashed' />

                      <div className='form-group'>
                        <div className='col-lg-2' />
                        <div className='col-lg-10'>
                          <button
                            className='btn btn-white'
                            onClick={this.resetClick}
                          >
                            <span><LocalizedMessage id='reset' /></span>
                          </button>
                          &nbsp;&nbsp;
                          <button
                            type='submit'
                            className='btn btn-primary'
                            onClick={this.onSubmit}
                            disabled={!country || !country.value || !region || !region.length}
                          >
                            <>
                              <i className='fa fa-chevron-down fa-lg' />
                              <span><LocalizedMessage id='save' /></span>
                            </>
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-12'>
              <div className='ibox float-e-margins'>
                <div className='ibox-content'>
                  <div className='table-responsive'>
                    <TableList
                      currentPage={filters.currentPage}
                      currentSort={filters.currentSort}
                      totalItems={dataListTotal}
                      itemsCountPerPage={filters.max}
                      onUpdateSort={onUpdateSort}
                      onUpdatePage={onUpdatePage}
                      isLoading={isLoading}
                      head={[
                        {
                          label: localizeMessage({ id: 'id' }),
                          sort: 'id'
                        },
                        {
                          label: localizeMessage({ id: 'country' }),
                          sort: 'country'
                        },
                        {
                          label: localizeMessage({ id: 'region' }),
                          sort: 'name'
                        }
                      ]}
                    >
                      {
                        dataList.map((row, rowIndex) => (
                          <tr key={rowIndex + '_regions'}>
                            <td>{row.id}</td>
                            <td>{row.country ? row.country[countryFieldName] : '-'}</td>
                            <td>{row.name}</td>
                          </tr>
                        ))
                      }
                    </TableList>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default URLFiltersHoc(Regions, 'id');
